import AddressSwitcherBar from 'components/address-switcher-bar/AddressSwitcherBar.tsx'
import { Banner, Heading, Icon, LoadingSkeleton, Switch } from '@boltenergy-be/design-system'
import { checkIsEarlyBird, getUsableProducts } from 'utils/products.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { selectCurrentAccount, selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { UserTypes } from 'store/auth/types.ts'
import styles from './TariffsProducts.module.scss'
import { useState } from 'react'
import {
  CUSTOMER_TYPES,
  ENERGY_TYPES,
  getProductFromProductCode,
  Product,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCTS,
  PRODUCTS_CONFIG
} from '@boltenergy-be/shared'
import { useGetProductsQuery } from 'store/queries/bolt-api/products'
import ProductCard from 'pages/app/tariffs/products/components/product-card/ProductCard.tsx'
import { useTranslation } from 'react-i18next'
import { checkProductIsDisabled } from 'pages/app/tariffs/products/utils.ts'
import ProductSwitchModal from 'pages/app/tariffs/products/components/product-switch-modal/ProductSwitchModal.tsx'
import { selectPriceLists } from 'store/contact/selectors.ts'

const TariffsProducts = () => {
  // Redux
  const serviceContracts = useStoreSelector(selectCurrentServiceContracts)
  const priceListBreakdown = useStoreSelector(selectPriceLists)
  const account = useStoreSelector(selectCurrentAccount)

  // Redux queries
  const { data: productsData, isLoading: loadingProductsData } = useGetProductsQuery()

  // i18n
  const { t } = useTranslation(['tariffs', 'common'])
  const lowerCaseLanguage = useGetLanguage('lowercase')

  // Local state
  const [switchModalProduct, setSwitchModalProduct] = useState<Product | null>(null)

  // Constants
  const currentProduct = getProductFromProductCode(serviceContracts.electricity.detail.productCode)
  const currentCustomerType = account?.company?.enterpriseNumber ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential
  const isEarlyBird = checkIsEarlyBird()
  const usableProducts = getUsableProducts({
    isEarlyBird,
    region: serviceContracts.electricity.deliveryPoint.region,
    userType: UserTypes.SUPER_USER,
    withBoltGo: false,
    plentyProducts: true
  })
  const usableProductsWithCounterparts = usableProducts
    .map((product) => {
      const counterpart = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.counterpart]

      if (counterpart) {
        return [product, counterpart]
      } else {
        return product
      }
    })
    .flat()

  // Showable products
  const [showUnavailableProducts, setShowUnavailableProducts] = useState<boolean>(
    !!currentProduct && checkProductIsDisabled(currentProduct, usableProductsWithCounterparts)
  )
  const productsToShow = Object.values(PRODUCTS).filter((product) =>
    showUnavailableProducts ? true : !checkProductIsDisabled(product, usableProductsWithCounterparts)
  )

  return (
    <>
      <AddressSwitcherBar>
        <div className="flex-col gap-200">
          <Heading as="h2" variant="h5">
            {serviceContracts?.gas
              ? `${t(($) => $.electricity, {
                  ns: 'common'
                })} & ${t(($) => $.gas, {
                  ns: 'common'
                })}`
              : t(($) => $.electricity, {
                  ns: 'common'
                })}
          </Heading>

          <div className={styles['ean-wrapper']}>
            <small className={styles.ean}>
              <Icon name="electricity" size={16} />{' '}
              <strong>
                {t(($) => $.ean, {
                  ns: 'common'
                })}
              </strong>
              : {serviceContracts.electricity.deliveryPoint.ean}
            </small>

            {!!serviceContracts.gas && (
              <small className={styles.ean}>
                <Icon name="gas" size={16} />{' '}
                <strong>
                  {t(($) => $.ean, {
                    ns: 'common'
                  })}
                </strong>
                : {serviceContracts.gas.deliveryPoint.ean}
              </small>
            )}
          </div>
        </div>
      </AddressSwitcherBar>

      <section>
        <header className={styles['product-overview-header']}>
          <Heading as="h2" variant="h3" className={styles.title}>
            {t(($) => $.products.title)}
          </Heading>

          <small className={styles.total}>
            {t(($) => $.products.total, {
              count: productsToShow.length
            })}
          </small>

          <Switch
            checked={showUnavailableProducts}
            onChange={(event) => setShowUnavailableProducts(event.target.checked)}
            label={t(($) => $.products.toggleAvailability)}
            className={styles.toggle}
          />
        </header>

        {loadingProductsData ? (
          <LoadingSkeleton className={styles['card-list']}>
            <LoadingSkeleton.Rectangle radius="lg" width="100%" aspectRatio="4 / 3" />
            <LoadingSkeleton.Rectangle radius="lg" width="100%" aspectRatio="4 / 3" />
            <LoadingSkeleton.Rectangle radius="lg" width="100%" aspectRatio="4 / 3" />
          </LoadingSkeleton>
        ) : !productsData?.products ? (
          <Banner type="blocking" title="Pricelists were not found." />
        ) : (
          <ul className={styles['card-list']}>
            {productsToShow
              .sort((a, b) => {
                const aDisabled = checkProductIsDisabled(a, usableProductsWithCounterparts) ? 1 : 0
                const bDisabled = checkProductIsDisabled(b, usableProductsWithCounterparts) ? 1 : 0
                return aDisabled - bDisabled
              })
              .map((product) => {
                const disabled = checkProductIsDisabled(product, usableProductsWithCounterparts)
                const productCode = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.productCodes]
                return (
                  <ProductCard
                    key={product}
                    {...{ product, disabled }}
                    customerType={currentCustomerType}
                    handleSelectProduct={setSwitchModalProduct}
                    currentPriceList={priceListBreakdown?.electricity?.current}
                    pricelists={{
                      [ENERGY_TYPES.electricity]:
                        productsData.products.electricity[productCode[ENERGY_TYPES.electricity]].pricelists[currentCustomerType][
                          lowerCaseLanguage
                        ],
                      [ENERGY_TYPES.gas]: serviceContracts.gas
                        ? productsData.products.gas[productCode[ENERGY_TYPES.gas]].pricelists[currentCustomerType][lowerCaseLanguage]
                        : undefined
                    }}
                  />
                )
              })}
          </ul>
        )}
      </section>

      {!!switchModalProduct && (
        <ProductSwitchModal
          newProduct={switchModalProduct}
          isRenew={switchModalProduct === currentProduct}
          isOpen={!!switchModalProduct}
          onRequestClose={() => setSwitchModalProduct(null)}
        />
      )}
    </>
  )
}

export default TariffsProducts
